import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ImageUploader } from "@/components/ImageUploader";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, FolderTree } from "lucide-react";

const BLANK = { name: "", icon: "leaf", image: "", default_sort: "newest" };
const SORTS = [["newest", "Newest First"], ["popular", "Bestseller"], ["price_low", "Price Low–High"], ["price_high", "Price High–Low"], ["discount", "Highest Discount"], ["rating", "Best Rated"]];

export default function AdminCategories() {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/categories").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = () => {
    if (!editing.name.trim()) return toast.error("Category name required");
    const payload = { name: editing.name, icon: editing.icon || "leaf", image: editing.image || "", default_sort: editing.default_sort || "newest" };
    const req = editing.id ? api.put(`/admin/categories/${editing.id}`, payload) : api.post("/admin/categories", payload);
    req.then(() => { toast.success(editing.id ? "Category updated" : "Category created"); setEditing(null); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Save failed"));
  };
  const del = (c) => { if (!window.confirm(`Delete category "${c.name}"? Products keep their data but lose this grouping.`)) return; api.delete(`/admin/categories/${c.id}`).then(() => { toast.success("Category deleted"); load(); }); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <h1 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight">Categories</h1>
        <Button onClick={() => setEditing({ ...BLANK })} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="add-category-btn"><Plus className="w-4 h-4 mr-1" /> Add Category</Button>
      </div>

      {!list ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div> :
        list.length === 0 ? <p className="text-center py-16 text-muted-foreground"><FolderTree className="w-10 h-10 mx-auto mb-2 text-sage" />No categories yet.</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl soft-shadow overflow-hidden flex" data-testid={`admin-category-${c.id}`}>
              <div className="w-24 h-24 bg-sand shrink-0 overflow-hidden">{c.image ? <img src={c.image} alt={c.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><FolderTree className="w-7 h-7 text-sage" /></div>}</div>
              <div className="p-4 flex-1 min-w-0">
                <h3 className="font-heading text-lg text-forest truncate">{c.name}</h3>
                <p className="text-xs text-olive">{c.product_count ?? 0} products</p>
                <p className="text-[11px] text-brown/60 mt-0.5">Sort: {(SORTS.find(([v]) => v === (c.default_sort || "newest")) || ["", "Newest First"])[1]}</p>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...BLANK, ...c })} data-testid={`edit-category-${c.id}`} className="rounded-full border-sage flex-1"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                  <button onClick={() => del(c)} data-testid={`delete-category-${c.id}`} className="w-8 h-8 rounded-full border border-sage flex items-center justify-center shrink-0"><Trash2 className="w-4 h-4 text-terracotta" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-cream max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-forest">{editing?.id ? "Edit" : "Add"} Category</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div><Label className="text-sm mb-1 block">Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-xl bg-white" data-testid="cf-name" /></div>
              <div><Label className="text-sm mb-1 block">Icon (lucide name, e.g. leaf, apple, heart)</Label><Input value={editing.icon} onChange={(e) => setEditing({ ...editing, icon: e.target.value })} className="rounded-xl bg-white" placeholder="leaf" /></div>
              <div><Label className="text-sm mb-1.5 block">Category Image</Label><ImageUploader value={editing.image} onChange={(url) => setEditing((cur) => ({ ...cur, image: url }))} label="Upload category image" /></div>
              <div><Label className="text-sm mb-1 block">Default Product Sort (storefront)</Label>
                <Select value={editing.default_sort || "newest"} onValueChange={(v) => setEditing({ ...editing, default_sort: v })}>
                  <SelectTrigger className="rounded-xl bg-white" data-testid="cf-default-sort"><SelectValue /></SelectTrigger>
                  <SelectContent>{SORTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={save} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="save-category-btn">Save Category</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
